<template>
    <modal-side-generic
    :title="productSelected.title"
    :subTitle="productSelected.description"
    :triggerClose="triggerClose"
    :buttonsFooterData="buttonsFooterData"
    :showFooter="showFooter"
    @hideMe="[triggerClose = false, emit('closeMe')]">
        <div v-for="(lineGroup, index) in detailsArray" :key="`lineGroup${lineGroup.index}`" class="group-wrapper">
            <!-- Divider -->
            <divider-generic :icon="lineGroup.icon" :title="lineGroup.label" :applyBorder="index !== 0"/>
            <div v-for="(line, index) in lineGroup.lines" :key="`lineGroupLine${line,index}`" class="line-content">
                <!-- Text Block -->
                <text-block-generic v-if="line.content" :params="line" :mask="line.mask" />
                <!-- Info Sheet Button -->
                <button-tertiary
                v-if="line.buttonText"
                class="button"
                :label="line.buttonText"
                :icon="line.buttonIcon"
                @clicked="line.buttonClicked"/>
            </div>
        </div>
    </modal-side-generic>
    <!-- Component to handle download logic based on platform -->
    <download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Vue */
import { ref, computed, onMounted } from 'vue'

/* Assets */
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['closeMe', 'chargeProduct', 'mobilizeProduct'])

/* Props received:
@productSelected { Object }
*/
const props = defineProps({
    productSelected: Object
})

/* Data */
const triggerClose = ref(false)
const downloadLink = ref(null)

/*************************************************
*                 Footer Button                  *
*************************************************/
const showFooter = ref(false)

const buttonsFooterData = computed(() => {
    const buttons = []

    if (props.productSelected.termAccountProduct.canBeIncreased) {
        buttons.push({
            label: t('savings.mySavings.portfolio.mobilize'),
            buttonPosition: 'left',
            callbackFunction: () => { emit('mobilizeProduct') }
        })
    }

    if (props.productSelected.termAccountProduct.canBeIncreased) {
        buttons.push({
            label: t('savings.mySavings.portfolio.charge'),
            buttonType: 'primary',
            callbackFunction: () => { emit('chargeProduct') }
        })
    }

    return buttons
})

const infoSheetClicked = () => {
    downloadLink.value = props.productSelected.termAccountProduct.infoSheetUrl
}

/* Details Data */
const detailsArray = computed(() => {
    return [
        /* Product Informations Group */
        {
            label: t('savings.mySavings.portfolio.modal.productInformationTitle'),
            icon: 'coin-drop',
            lines: [{
                label: t('savings.mySavings.portfolio.modal.productNameLabel'),
                content: props.productSelected.termAccountProduct.name || '-'
            }, {
                content: props.productSelected.termAccountProduct.description
            }, {
                buttonText: t('savings.mySavings.portfolio.modal.infoSheetBtn'),
                buttonType: 'primary',
                buttonIcon: 'download',
                buttonClicked: infoSheetClicked
            }, {
                label: t('savings.mySavings.portfolio.modal.minimumAmountLabel'),
                content: amountFormat(props.productSelected.termAccountProduct.minimumAmount, props.productSelected.currency)
            }, {
                label: t('savings.mySavings.portfolio.modal.maximumAmountLabel'),
                content: amountFormat(props.productSelected.termAccountProduct.maximumAmount, props.productSelected.currency)
            }, {
                label: t('savings.mySavings.portfolio.modal.tanbLabel'),
                content: props.productSelected.interestRate ? `${props.productSelected.interestRate}%` : '-'
            }, {
                label: t('savings.mySavings.portfolio.modal.creationDateLabel'),
                content: getDate(props.productSelected.creationDate),
                classes: 'capitalize'
            }, {
                label: t('savings.mySavings.portfolio.modal.maturityDateLabel'),
                content: getDate(props.productSelected.maturityDate),
                classes: 'capitalize'
            }, {
                label: t('savings.mySavings.portfolio.modal.durationLabel'),
                content: t('savings.mySavings.portfolio.modal.durationContent',
                { numberOfDays: props.productSelected.numberOfDays, ageInDays: props.productSelected.ageInDays })
            }]
        },
        /* Amount Group */
        {
            label: t('savings.mySavings.portfolio.modal.amountTitle'),
            icon: 'bills',
            lines: [{
                label: t('savings.mySavings.portfolio.modal.balanceLabel'),
                content: amountFormat(props.productSelected.balance, props.productSelected.currency)
            }]
        }
    ]
})

onMounted(() => {
    if (props.productSelected.termAccountProduct.canBeIncreased) showFooter.value = true
})
</script>

<style lang="scss" scoped>
</style>
